import React, { useRef } from 'react';
import Swal from '../librarianSwal';
import useModalA11y from '../components/useModalA11y';
import useRegisterMemberWorkflow from './useRegisterMemberWorkflow';
import RegisterMemberForm from './RegisterMemberForm';
import CollectPaymentModal from './CollectPaymentModal';
import './RegisterMemberModal.css';

const FORM_ID = 'register-member-form';

// Owns the Register Member popup: the form step, the post-registration
// success step, and the handoff into the shared CollectPaymentModal. Members
// page owns whether this is mounted at all (open/onClose); everything about
// the registration itself lives in useRegisterMemberWorkflow so this modal
// and the backward-compatible full-page route never duplicate that logic.
const RegisterMemberModal = ({ open, onClose, onRegistered, triggerRef }) => {
  const containerRef = useRef(null);
  const wf = useRegisterMemberWorkflow();

  const attemptClose = async (force = false) => {
    if (wf.saving) return;

    if (!force && wf.hasUnsavedChanges()) {
      const confirmed = await Swal.fire({
        title: 'Discard this registration?',
        text: 'The details you entered will be lost.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, discard',
        cancelButtonText: 'Keep editing'
      });
      if (!confirmed.isConfirmed) return;
    }

    wf.resetAll();
    onClose();
  };

  const handleBackdropClick = () => {
    if (wf.saving || wf.hasUnsavedChanges()) return;
    wf.resetAll();
    onClose();
  };

  const handleDone = () => {
    wf.resetAll();
    onClose();
    onRegistered?.();
  };

  useModalA11y({
    isOpen: open,
    onEscape: () => attemptClose(),
    containerRef,
    restoreFocusRef: triggerRef
  });

  if (!open) return null;

  // Once registration has succeeded and the librarian opts to collect the
  // deposit now, hand off straight to the shared payment modal instead of
  // reimplementing payment collection here.
  if (wf.showPaymentModal && wf.result) {
    return (
      <CollectPaymentModal
        memberId={wf.result.member_id}
        membershipId={wf.result.membership_id}
        amount={wf.result.deposit_required_amount}
        memberName={`${wf.form.first_name} ${wf.form.last_name}`.trim()}
        membershipNo={wf.result.membership_no}
        purpose="Membership Security Deposit"
        onClose={() => wf.setShowPaymentModal(false)}
        onSuccess={() => {
          wf.setPaymentStatus('success');
          handleDone();
        }}
        onOrderCreated={(orderId) => wf.setLastOrderId(orderId)}
      />
    );
  }

  const depositRequired = Number(wf.result?.deposit_required_amount || 0);
  const canShowPayment = wf.result && depositRequired > 0 && wf.paymentStatus !== 'success' && wf.canCollectPayment;

  return (
    <div className="rm-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && handleBackdropClick()}>
      <div className="rm-modal" role="dialog" aria-modal="true" aria-labelledby="rm-modal-title" ref={containerRef}>
        <div className="rm-modal__header">
          <div>
            <h2 className="rm-modal__title" id="rm-modal-title">Register Member</h2>
            <div className="rm-modal__subtitle">
              {wf.result
                ? 'Registration complete'
                : 'Register a school parent or outsider and collect their membership payment'}
            </div>
          </div>
          <button
            type="button"
            className="rm-modal__close"
            onClick={() => attemptClose()}
            disabled={wf.saving}
            aria-label="Close Register Member dialog"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="rm-modal__body">
          {!wf.result ? (
            <RegisterMemberForm
              formId={FORM_ID}
              form={wf.form}
              onChange={wf.handleChange}
              onMemberSourceChange={wf.handleMemberSourceChange}
              isSchoolParent={wf.isSchoolParent}
              onSubmit={wf.handleRegister}
              profileImageFile={wf.profileImageFile}
              onProfileImageChange={wf.handleProfileImageChange}
              outsiderDepositAmount={wf.outsiderDepositAmount}
            />
          ) : (
            <div className="rm-success">
              <div className="rm-success__icon">
                <i className="fas fa-check-circle" />
              </div>
              <div className="rm-success__name">{wf.form.first_name} {wf.form.last_name}</div>
              <div className="rm-success__row">
                <span>Membership No.</span>
                <strong>{wf.result.membership_no}</strong>
              </div>
              <div className="rm-success__row">
                <span>Membership Status</span>
                <span className={`badge ${wf.paymentStatus === 'success' ? 'bg-success' : 'bg-warning text-dark'}`}>
                  {wf.paymentStatus === 'success' ? 'Active' : 'Pending Payment'}
                </span>
              </div>
              {depositRequired > 0 && (
                <div className="rm-success__row">
                  <span>Required Deposit</span>
                  <strong>₹{depositRequired}</strong>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="rm-modal__footer">
          {!wf.result ? (
            <>
              <button type="button" className="btn btn-outline-secondary" onClick={() => attemptClose()} disabled={wf.saving}>
                Cancel
              </button>
              <button
                type="submit"
                form={FORM_ID}
                className="btn btn-primary"
                disabled={wf.saving || !wf.form.member_source}
              >
                {wf.saving ? (
                  <><span className="spinner-border spinner-border-sm me-2" />Registering...</>
                ) : (
                  'Register Member'
                )}
              </button>
            </>
          ) : canShowPayment ? (
            <>
              <button type="button" className="btn btn-outline-secondary" onClick={handleDone}>
                Pay Later
              </button>
              {wf.lastOrderId && (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={wf.handleCheckStatus}
                  disabled={wf.checkingStatus}
                >
                  {wf.checkingStatus ? 'Checking...' : 'Check Payment Status'}
                </button>
              )}
              <button type="button" className="btn btn-primary" onClick={() => wf.setShowPaymentModal(true)}>
                {wf.lastOrderId ? 'Retry Payment' : 'Collect Payment'}
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-primary" onClick={handleDone}>
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterMemberModal;
